import { EnergyType, State } from '@energy-retailer/constants';
import {
  type FeedInRatesSchema,
  type PlanSchema,
} from '@energy-retailer/schema';
import Link from 'next/link';
import { type ComponentProps } from 'react';

import { getPlanDocsURL } from '@/app/lib/utils/get-plan-docs-url';
import { Collapse } from '@/components/collapse';
import { Text } from '@/components/text';
import { TERMS_CONDITIONS_URL } from '@/constants';
import { getProductPlanEstimatedPrice } from '@/lib/utils';

import { ProductPlanCardBanner } from './banner';
import { ProductPlanCardButtons } from './buttons';
import { ProductPlanCardDescription } from './description';
import { ProductPlanCardFeatures } from './features';
import { PlanDetailListings } from './plan-detail-listing';
import { cardVariants } from './style';
import { TariffInformation } from './tariff-information';
import { TariffQuoteText } from './tariff-quote-text';
import { PlanTariffTooltip } from './tariff-tooltip';
import { ProductPlanCardTitle } from './title';

type ProductPlanProps = {
  plan: PlanSchema;
  includeSolarFeedTariff?: boolean;
  setAddAddressActive: (addAddressActive: boolean) => void;
} & Omit<
  ComponentProps<typeof ProductPlanCardButtons>,
  'planId' | 'planSlug' | 'product' | 'fluxPlanId'
> &
  ComponentProps<typeof ProductPlanCardBanner> &
  Pick<ComponentProps<typeof PlanDetailListings>, 'priceFrequency'>;

const ProductPlanCard = ({
  plan,
  addAddress,
  setAddAddressActive,
  hasAddress,
  selected = false,
  priceFrequency,
  includeSolarFeedTariff = true,
}: ProductPlanProps) => {

  const price = getProductPlanEstimatedPrice({
    estimates: plan.estimates,
    priceFrequency,
  });

  const { controlledLoad, feedInRates } = plan as PlanSchema & {
    controlledLoad: boolean;
    feedInRates?: FeedInRatesSchema;
  };

  const planDocUrl = getPlanDocsURL(plan);

  return (
    <div
      className={cardVariants({ planType: plan.product.productType })}
      data-testid={plan.product.name.toLowerCase().replaceAll(' ', '-')}
    >
      <ProductPlanCardBanner bannerText={plan.product.banner} />

      <div className="grid grid-cols-12 gap-b p-c">
        <div className="col-span-12 flex flex-col gap-b lg:col-span-7 xl:col-span-8">
          <ProductPlanCardTitle product={plan.product} />
          <ProductPlanCardFeatures product={plan.product} />
          <PlanDetailListings
            state={plan.state}
            price={price}
            comparison={
              plan.energyType === EnergyType.Electricity
                ? plan.comparison
                : undefined
            }
            superOffPeakTariff={plan.rates.superOffPeak}
            vdoTooltip={plan.offerTooltip}
            priceFrequency={priceFrequency}
            hasAddress={hasAddress}
            addAddress={addAddress}
            setAddAddressActive={setAddAddressActive}
          />
          {hasAddress ? <PlanTariffTooltip plan={plan} /> : null}
        </div>
        <div className="col-span-12 mt-b flex flex-col gap-b rounded-rad-a bg-gray-50 p-b lg:col-span-5 lg:mt-0 xl:col-span-4">
          {hasAddress ? (
            <Text
              variant="h3"
              as="h3"
            >
              Choose this plan
            </Text>
          ) : (
            <Text
              variant="h3"
              as="h3"
            >
              Plan links
            </Text>
          )}

          <ProductPlanCardDescription
            description={plan.product.description}
            termsLink={plan.product.termsLink}
            productType={plan.product.productType}
            fairUseLink={plan.product.fairUseLink}
          />

          <div className="flex flex-col gap-a lg:mt-auto">
            <Link
              className="text-info underline decoration-info"
              href={TERMS_CONDITIONS_URL}
            >
              Powershop Terms & Conditions
            </Link>
            <Link
              className="text-info underline decoration-info"
              href={planDocUrl}
              target="_blank"
            >
              {plan.state === State.VIC
                ? 'Energy Fact Sheet'
                : 'Basic Plan Information'}
            </Link>
            <ProductPlanCardButtons
              product={plan.product}
              planId={plan.planID}
              planSlug={plan.product.slug}
              fluxPlanId={plan.product.id}
              addAddress={addAddress}
              setAddAddressActive={setAddAddressActive}
              hasAddress={hasAddress}
              selected={selected}
            />
          </div>
        </div>
        {hasAddress ? (
          <div className="col-span-12">
            <Collapse
              variant="minimal"
              title="Plan details: Pricing, rates & tariffs"
            >
              <div className="grid grid-cols-12 gap-b py-b lg:gap-e">
                <div className="col-span-12 flex flex-col gap-b lg:col-span-8">
                  <Text
                    variant="h4"
                    as="h4"
                  >
                    {plan.energyType} rates and pricing
                  </Text>
                  <TariffQuoteText
                    docsHref={planDocUrl}
                    hasAddress={hasAddress}
                    state={plan.state}
                  />
                  <TariffInformation
                    planName={plan.product.name}
                    energyType={plan.energyType}
                    includeSolarFeedTariff={includeSolarFeedTariff}
                    controlledLoad={controlledLoad as boolean}
                    rates={plan.rates}
                    feedInRates={feedInRates as FeedInRatesSchema}
                  />
                </div>

                <div className="hidden lg:contents">
                  <div className="col-span-12 flex flex-col gap-b lg:col-span-4">
                    <Text
                      variant="h4"
                      as="h4"
                    >
                      Offer Details
                    </Text>
                    {plan.product.offerDetails ? (
                      <Text
                        variant="body2"
                        weight="semi"
                      >
                        {plan.product.offerDetails}
                      </Text>
                    ) : null}
                    <Text variant="body2">
                      <Text
                        variant="body2"
                        weight="semi"
                        as="span"
                      >
                        Track usage:
                      </Text>{' '}
                      By signing up you automatically get access to the Powershop
                      app to manage your bills and energy usage.
                    </Text>
                    <Text variant="body2">
                      <Text
                        variant="body2"
                        weight="semi"
                        as="span"
                      >
                        No exit fees:
                      </Text>{' '}
                      This offer is an ongoing no lock-in contract with no exit
                      fees.
                    </Text>
                  </div>
                </div>
              </div>
            </Collapse>
            
            <div className="block lg:hidden">
              <Collapse
                variant="minimal"
                title="Plan features">
                <div className="col-span-12 flex flex-col gap-b lg:col-span-4 pt-2">
                    <Text
                      variant="h4"
                      as="h4"
                    >
                      Offer Details
                    </Text>
                    {plan.product.offerDetails ? (
                      <Text variant="body2" weight="semi">
                        {plan.product.offerDetails}
                      </Text>
                    ) : null}
                    <Text variant="body2">
                      <Text
                        variant="body2"
                        weight="semi"
                        as="span"
                      >
                        Track usage:
                      </Text>{' '}
                      By signing up you automatically get access to the Powershop
                      app to manage your bills and energy usage.
                    </Text>
                    {/* This does not need to be implemented yet
                    <Text variant="body2">
                      <Text
                        variant="body2"
                        weight="semi"
                        as="span"
                      >
                      Early payment options:
                      </Text>{' '}
                      Flexible payment options to fit your needs.
                    </Text>
                    */}
                    <Text variant="body2">
                      <Text
                        variant="body2"
                        weight="semi"
                        as="span"
                      >
                        No exit fees:
                      </Text>{' '}
                      This offer is an ongoing no lock-in contract with no exit
                      fees.
                    </Text>
                  </div>
              </Collapse>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export { ProductPlanCard };
